import {
  createContext,
  PropsWithChildren,
  useCallback,
  useMemo,
  useState,
} from "react";

import {
  AI_SERVICE_UNAVAILABLE_GLOBAL_ERROR_CODES,
  DOCUMENT_ERROR_EXCLUDED_MESSAGES,
  GLOBAL_ERROR_MESSAGES,
  DEFAULT_ERROR_MESSAGE,
  DEFAULT_DOCUMENT_ERROR_MESSAGE,
  ERROR_KEYS,
} from "../constants";

import type {
  DocumentErrorExcludedCode,
  GlobalErrorCode,
  ErrorKey,
} from "../types";

type ErrorObjectValue = {
  error: Error | string | null;
  details?: string;
};

type ErrorInputValue =
  | Error
  | string
  | null
  | GlobalErrorCode
  | ErrorObjectValue;

type AppErrorValue = {
  message: string;
  details?: string;
};

type ContextErrorValue = {
  type: ErrorKey;
  message: string;
  details?: string;
};

type ErrorContextShape = {
  error: ContextErrorValue | null;
  isAiServiceUnavailable: boolean;
  isGlobalErrorCode: (value: string) => value is GlobalErrorCode;
  setError: (type: ErrorKey, error: ErrorInputValue, details?: string) => void;
  setMultipleErrors: (
    errors: Partial<Record<ErrorKey, ErrorInputValue>>,
  ) => void;
  clearError: (type: ErrorKey) => void;
  clearAllErrors: () => void;
  mapDocumentErrorExcluded: (
    code: DocumentErrorExcludedCode | string,
  ) => string;
  mapGlobalError: (code: GlobalErrorCode) => string;
};

type NormalizedErrorValue =
  | null
  | {
      kind: "global";
      code: GlobalErrorCode;
      details?: string;
    }
  | {
      kind: "message";
      message: string;
      details?: string;
    };

type ErrorState = Record<ErrorKey, NormalizedErrorValue>;

const mapGlobalError = (code: GlobalErrorCode): string =>
  String(GLOBAL_ERROR_MESSAGES[code] ?? DEFAULT_ERROR_MESSAGE);

// A stored error message can be a single code ("ERR-G-002"), a code embedded
// in a longer string ("ERR-AIE: Activity task failed"), or several codes
// joined together ("ERR-DOWN; ERR-CARRIER") when a non-fatal warning was
// collected before the code that actually failed the job. The earliest
// (leftmost) matching key wins so a leading, decisive code is never masked
// by one that happens to sort earlier in this object (CD-2480) — and among
// keys tied for the same position, the longest wins, so "WAR-EXCL-REASONS-2"
// is never shadowed by its own prefix "WAR-EXCL-REASONS".
const mapDocumentErrorExcluded = (
  code: DocumentErrorExcludedCode | string,
): string => {
  let bestKey: DocumentErrorExcludedCode | null = null;
  let bestIndex = Infinity;

  for (const key of Object.keys(
    DOCUMENT_ERROR_EXCLUDED_MESSAGES,
  ) as DocumentErrorExcludedCode[]) {
    const index = code.indexOf(key);
    if (index === -1) {
      continue;
    }
    if (
      index < bestIndex ||
      (index === bestIndex && (!bestKey || key.length > bestKey.length))
    ) {
      bestKey = key;
      bestIndex = index;
    }
  }

  return bestKey
    ? String(DOCUMENT_ERROR_EXCLUDED_MESSAGES[bestKey])
    : DEFAULT_DOCUMENT_ERROR_MESSAGE;
};

const isGlobalErrorCode = (value: string): value is GlobalErrorCode =>
  Object.prototype.hasOwnProperty.call(GLOBAL_ERROR_MESSAGES, value);

const isErrorObjectValue = (
  value: ErrorInputValue,
): value is ErrorObjectValue =>
  typeof value === "object" && value !== null && "error" in value;

const normalizeErrorInput = (
  errorInput: ErrorInputValue,
  details?: string,
): NormalizedErrorValue => {
  if (errorInput === null) {
    return null;
  }

  if (typeof errorInput === "string") {
    return isGlobalErrorCode(errorInput)
      ? { kind: "global", code: errorInput, details }
      : { kind: "message", message: errorInput, details };
  }

  if (errorInput instanceof Error) {
    return {
      kind: "message",
      message: errorInput.message,
      details,
    };
  }

  if (isErrorObjectValue(errorInput)) {
    if (errorInput.error === null) {
      return null;
    }

    if (
      typeof errorInput.error === "string" &&
      isGlobalErrorCode(errorInput.error)
    ) {
      return {
        kind: "global",
        code: errorInput.error,
        details: errorInput.details ?? details,
      };
    }

    return {
      kind: "message",
      message:
        errorInput.error instanceof Error
          ? errorInput.error.message
          : String(errorInput.error),
      details: errorInput.details ?? details,
    };
  }

  return {
    kind: "message",
    message: DEFAULT_ERROR_MESSAGE,
    details,
  };
};

const toAppError = (errorInput: NormalizedErrorValue): AppErrorValue | null => {
  if (errorInput === null) {
    return null;
  }

  if (errorInput.kind === "message") {
    return {
      message: errorInput.message,
      details: errorInput.details,
    };
  }

  return {
    message: mapGlobalError(errorInput.code),
    details: errorInput.details,
  };
};

const toContextError = (
  type: ErrorKey,
  error: AppErrorValue,
): ContextErrorValue => ({
  type,
  message: error.message,
  details: error.details,
});

export const ErrorContext = createContext<ErrorContextShape | undefined>(
  undefined,
);

const getHighestPriorityError = (
  errors: ErrorState,
): ContextErrorValue | null => {
  for (const type of ERROR_KEYS) {
    const currentError = toAppError(errors[type]);
    if (currentError) {
      return toContextError(type, currentError);
    }
  }

  return null;
};

const getIsAiServiceUnavailable = (errors: ErrorState): boolean =>
  Object.values(errors).some(
    (errorInput) =>
      errorInput?.kind === "global" &&
      AI_SERVICE_UNAVAILABLE_GLOBAL_ERROR_CODES.has(errorInput.code),
  );

export const ErrorProvider = ({
  children,
}: PropsWithChildren): React.JSX.Element => {
  const [errors, setErrors] = useState<ErrorState>({
    general: null,
    upload: null,
    review: null,
    docs: null,
  });

  const contextError = getHighestPriorityError(errors);

  const setError = useCallback(
    (type: ErrorKey, error: ErrorInputValue, details?: string): void => {
      setErrors((prev) => ({
        ...prev,
        [type]: normalizeErrorInput(error, details),
      }));
    },
    [],
  );

  const setMultipleErrors = useCallback(
    (newErrors: Partial<Record<ErrorKey, ErrorInputValue>>): void => {
      setErrors((prev) => {
        const updated = { ...prev };

        for (const type of Object.keys(newErrors) as ErrorKey[]) {
          if (newErrors[type] !== undefined) {
            updated[type] = normalizeErrorInput(
              newErrors[type] as ErrorInputValue,
            );
          }
        }

        return updated;
      });
    },
    [],
  );

  const clearError = useCallback((type: ErrorKey): void => {
    setErrors((prev) => ({
      ...prev,
      [type]: null,
    }));
  }, []);

  const clearAllErrors = useCallback((): void => {
    setErrors({
      general: null,
      upload: null,
      review: null,
      docs: null,
    });
  }, []);

  const value: ErrorContextShape = useMemo(
    () => ({
      error: contextError,
      isAiServiceUnavailable: getIsAiServiceUnavailable(errors),
      isGlobalErrorCode,
      setError,
      setMultipleErrors,
      clearError,
      clearAllErrors,
      mapDocumentErrorExcluded,
      mapGlobalError,
    }),
    [
      contextError,
      errors,
      setError,
      setMultipleErrors,
      clearError,
      clearAllErrors,
    ],
  );

  return (
    <ErrorContext.Provider value={value}>{children}</ErrorContext.Provider>
  );
};
